import React, { useMemo, useState } from "react";
import { PaeiBars, ROLE_META, fmtMinutes, isNum } from "./paeiPrimitives";

// The derived requirement per step: what the work needs, how it was read, and the
// audit trail behind every level. `reasons[]` is what makes a disputed level
// arguable, so it stays on the page (collapsed) rather than in a tooltip.

function RequirementRow({ task }) {
  const role = task.dominant_role;
  const shifted = Array.isArray(task.shifted_by) && task.shifted_by.length > 0;

  return (
    <tr>
      <td className="pa-cell-step">
        <div className="pa-step-name">{task.task_name}</div>
        <div className="pa-req-badges">
          <span className={`pa-tag src-${task.source}`}>{task.source}</span>
          {task.confidence === "low" && <span className="pa-tag low">low confidence</span>}
          {shifted && (
            <span className="pa-tag shifted" title={task.shifted_by.join(", ")}>
              shifted by {task.shifted_by.join(", ")}
            </span>
          )}
        </div>
        {Array.isArray(task.reasons) && task.reasons.length > 0 && (
          <details className="pa-reasons">
            <summary>why these levels</summary>
            <ul>
              {task.reasons.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          </details>
        )}
      </td>

      <td className="pa-num">{fmtMinutes(task.process_time)}</td>
      <td className="pa-num">{isNum(task.waiting_time) && Number(task.waiting_time) > 0 ? fmtMinutes(task.waiting_time) : "—"}</td>
      <td className="pa-num">{isNum(task.rework_time) && Number(task.rework_time) > 0 ? fmtMinutes(task.rework_time) : "—"}</td>

      <td>
        <span className={`pa-need role-${role}`}>
          {task.dominant_role_label || ROLE_META[role]?.label || "—"}
        </span>
      </td>

      <td className="pa-cell-bars">
        <PaeiBars values={task.required} compact />
        {task.required_before_shift && (
          <div className="pa-before-shift">
            before the shift:{" "}
            {["p", "a", "e", "i"].map((k) => (
              <span key={k}>
                {ROLE_META[k].letter} {Math.round(Number(task.required_before_shift[k] ?? 0))}
                {k === "i" ? "" : " · "}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}

function PaeiRequirementsTab({ result }) {
  const asIsTasks = useMemo(() => result?.as_is?.tasks || [], [result]);
  const redesignTasks = useMemo(() => result?.redesign?.tasks || [], [result]);
  const redesignAvailable = result?.redesign?.available !== false && redesignTasks.length > 0;

  const [stage, setStage] = useState(redesignAvailable ? "redesigned" : "as_is");
  const tasks = stage === "redesigned" && redesignAvailable ? redesignTasks : asIsTasks;

  const mixed = useMemo(() => {
    const sources = new Set(tasks.map((t) => t.source).filter(Boolean));
    return sources.size > 1;
  }, [tasks]);

  return (
    <div className="pa-panel">
      <div className="pa-toolbar">
        <div className="pa-seg" role="group" aria-label="Requirement stage">
          <button type="button" aria-pressed={stage === "as_is"} onClick={() => setStage("as_is")}>
            As declared <em>{asIsTasks.length}</em>
          </button>
          <button
            type="button"
            aria-pressed={stage === "redesigned"}
            disabled={!redesignAvailable}
            onClick={() => setStage("redesigned")}
          >
            After redesign <em>{redesignTasks.length}</em>
          </button>
        </div>
      </div>

      <p className="pa-card-hint">
        The capability each step demands, derived from its description and its timings, then moved by
        the goal weights and — on the redesigned view — by any heuristic that changed the work.
        {mixed ? " A mixed run is normal: rows read by the model and rows that fell back to keywords sit side by side." : ""}
      </p>

      {tasks.length === 0 ? (
        <div className="pa-empty">No steps to show.</div>
      ) : (
        <div className="pa-tbl-scroll">
          <table className="pa-tbl">
            <thead>
              <tr>
                <th>Step · why these levels</th>
                <th className="pa-num">Work</th>
                <th className="pa-num">Wait</th>
                <th className="pa-num">Rework</th>
                <th>Leading role</th>
                <th>Required levels</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t, i) => (
                <RequirementRow key={`${t.task_id}-${i}`} task={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PaeiRequirementsTab;
